import { useEffect, useMemo, useState } from "react";

type OverlapRow = {
  scorer: string;
  r1Position: string;
  r1Finish: string;
};

type SavedOverlap = {
  savedCount: number;
  bufferMinutes: number | null;
};

const TOURNAMENT_ID = "2026-british-masters";
const API_BASE = String(import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");

const BRITISH_MASTERS_R1: OverlapRow[] = [
  { scorer: "Michał Pietrzyk", r1Position: "G27 · pełne 18", r1Finish: "13:00" },
  { scorer: "Krzysztof Skowronek", r1Position: "G28 · pełne 18", r1Finish: "13:10" },
  { scorer: "Adam Orzechowski", r1Position: "G29 · pełne 18", r1Finish: "13:20" },
  { scorer: "Karol Mencel", r1Position: "G29 · pełne 18", r1Finish: "13:20" },
  { scorer: "Bartłomiej Salamon", r1Position: "G30 · 1–9", r1Finish: "11:00" },
  { scorer: "Igor Łagoda", r1Position: "G30 · 10–18", r1Finish: "13:30" },
  { scorer: "Filip Woźniak", r1Position: "G30 · pełne 18", r1Finish: "13:30" },
  { scorer: "Tomasz Omylak", r1Position: "G31 · 1–9", r1Finish: "11:10" },
  { scorer: "Sebastian Łubkowski", r1Position: "G31 · 10–18", r1Finish: "13:40" },
  { scorer: "Kamil Kosytorz", r1Position: "G32 · 1–9", r1Finish: "11:20" },
  { scorer: "Grzegorz Dańczyszyn", r1Position: "G32 · 10–18", r1Finish: "13:50" },
  { scorer: "Mateusz Kowal", r1Position: "G33 · 1–9", r1Finish: "11:30" },
  { scorer: "Piotr Mitas", r1Position: "G33 · 10–18", r1Finish: "14:00" },
  { scorer: "Daniel Heluszka", r1Position: "G34 · pełne 18", r1Finish: "14:10" },
  { scorer: "Piotr Banasik", r1Position: "G35 · pełne 18", r1Finish: "14:20" },
  { scorer: "Jakub Fiedeń", r1Position: "G36 · pełne 18", r1Finish: "14:30" },
  { scorer: "Piotr Zamojski", r1Position: "G37 · pełne 18", r1Finish: "14:40" },
  { scorer: "Łukasz Rojek", r1Position: "G38 · pełne 18", r1Finish: "14:50" },
  { scorer: "Szymon Kasprzak", r1Position: "G39 · pełne 18", r1Finish: "15:00" },
  { scorer: "Paweł Ślusarczyk", r1Position: "G40 · pełne 18", r1Finish: "13:00" },
  { scorer: "Mateusz Skorus", r1Position: "G41 · pełne 18", r1Finish: "13:10" },
  { scorer: "Henryk Bryniak", r1Position: "G42 · pełne 18", r1Finish: "13:20" },
  { scorer: "Piotr Korzeniowski", r1Position: "G43 · pełne 18", r1Finish: "13:30" },
  { scorer: "Mikołaj Rudnik", r1Position: "G44 · 10–18", r1Finish: "11:10" },
  { scorer: "Grzegorz Witkowski", r1Position: "G44 · 1–9", r1Finish: "13:40" },
  { scorer: "Mateusz Rogowski", r1Position: "G45 · 10–18", r1Finish: "11:20" },
  { scorer: "Marcel Brom", r1Position: "G45 · 1–9", r1Finish: "13:50" },
  { scorer: "Kacper Kuźma", r1Position: "G46 · 10–18", r1Finish: "11:30" },
  { scorer: "Piotr Łastawiecki", r1Position: "G46 · 1–9", r1Finish: "14:00" },
  { scorer: "Bartosz Goleniowski", r1Position: "G47 · pełne 18", r1Finish: "14:10" },
  { scorer: "Jacek Bułaczyński", r1Position: "G48 · pełne 18", r1Finish: "14:20" },
  { scorer: "Dennis Marcy", r1Position: "G49 · pełne 18", r1Finish: "14:30" },
  { scorer: "Jakub Opoka", r1Position: "G50 · pełne 18", r1Finish: "14:40" },
  { scorer: "Kamil Krawczyk", r1Position: "G51 · pełne 18", r1Finish: "14:50" },
  { scorer: "Jakub Górecki", r1Position: "G52 · pełne 18", r1Finish: "15:00" },
];

function addMinutes(time: string, minutes: number) {
  const [hours, mins] = time.split(":").map(Number);
  const total = hours * 60 + mins + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

async function overlapRequest<T>(method: "GET" | "PUT" | "DELETE", body?: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}/tournaments/${encodeURIComponent(TOURNAMENT_ID)}/r1-r2-overlap`, {
    method,
    credentials: "include",
    headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = payload?.detail;
    const message = typeof detail === "string" ? detail : detail?.message || `HTTP ${response.status}`;
    const errors = Array.isArray(detail?.errors) ? ` ${detail.errors.join("; ")}` : "";
    throw new Error(`${message}${errors}`);
  }
  return payload as T;
}

export function BritishMastersOverlapPanel() {
  const [mode, setMode] = useState<30 | 45>(45);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const rows = useMemo(
    () => BRITISH_MASTERS_R1.map((row) => ({
      ...row,
      hard: addMinutes(row.r1Finish, 30),
      safe: addMinutes(row.r1Finish, 45),
      selected: addMinutes(row.r1Finish, mode),
    })),
    [mode],
  );

  useEffect(() => {
    void overlapRequest<SavedOverlap>("GET")
      .then((result) => setMode(result.savedCount === BRITISH_MASTERS_R1.length && result.bufferMinutes === 30 ? 30 : 45))
      .catch(() => setMode(45));
  }, []);

  async function switchMode(next: 30 | 45) {
    if (next === mode || busy) return;
    setBusy(true); setError(""); setStatus("");
    try {
      if (next === 30) {
        const result = await overlapRequest<SavedOverlap>("PUT", {
          roundName: "R2",
          bufferMinutes: 30,
          rows: BRITISH_MASTERS_R1.map((row) => ({
            scorer: row.scorer,
            r1Position: row.r1Position,
            r1Finish: row.r1Finish,
            availableFrom: addMinutes(row.r1Finish, 30),
          })),
        });
        if (result.savedCount !== BRITISH_MASTERS_R1.length) throw new Error(`Zapisano tylko ${result.savedCount}/35 ograniczeń.`);
        setMode(30);
        setStatus("AWARYJNE +30 działa od razu dla wszystkich solverów.");
      } else {
        await overlapRequest("DELETE");
        setMode(45);
        setStatus("Przywrócono automatyczne SAFE +45 dla wszystkich solverów.");
      }
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : String(exc));
    } finally { setBusy(false); }
  }

  return <section className="generator-overlap-panel" aria-label="British Masters R1 do R2 — dostępność">
    <div className="generator-overlap-panel-head">
      <div>
        <span className="generator-overlap-kicker">BRITISH MASTERS 2026 · R1 → R2</span>
        <h3>Dostępność scorerów po dokończeniu R1</h3>
        <p>Nie wymaga zatwierdzania. Backend automatycznie dodaje te ograniczenia do każdego joba R2 przed wysłaniem go do Local, Google i obu VPS-ów.</p>
      </div>
      <div className="generator-overlap-mode-switch" role="group" aria-label="Bufor dostępności">
        <button type="button" className={mode === 45 ? "active" : ""} onClick={() => void switchMode(45)} disabled={busy}>SAFE +45</button>
        <button type="button" className={mode === 30 ? "active minimum" : "minimum"} onClick={() => void switchMode(30)} disabled={busy}>AWARYJNIE +30</button>
      </div>
    </div>

    <div className="generator-overlap-summary">
      <span><strong>35/35</strong> ograniczeń</span>
      <span><strong>{mode === 45 ? "+45 min" : "+30 min"}</strong> aktualny bufor</span>
      <span className={mode === 45 ? "generator-overlap-safe" : "generator-overlap-hard"}><strong>WYMUSZANE AUTOMATYCZNIE</strong> przez solver</span>
    </div>

    {busy ? <div className="generator-notice">Aktualizuję tryb dostępności…</div> : null}
    {status ? <div className="generator-notice">{status}</div> : null}
    {error ? <div className="generator-error">{error}</div> : null}

    <div className="generator-overlap-table-wrap">
      <table className="generator-overlap-table">
        <thead><tr><th>#</th><th>Scorer</th><th>Pozycja R1</th><th>Koniec R1</th><th>HARD +30</th><th>SAFE +45</th><th>R2 dostępny od</th></tr></thead>
        <tbody>{rows.map((row, index) => <tr key={`${row.scorer}-${row.r1Position}`}>
          <td>{index + 1}</td>
          <td><strong>{row.scorer}</strong></td>
          <td>{row.r1Position}</td>
          <td>{row.r1Finish}</td>
          <td>{row.hard}</td>
          <td>{row.safe}</td>
          <td><span className={mode === 45 ? "generator-overlap-safe" : "generator-overlap-hard"}>{row.selected}</span></td>
        </tr>)}</tbody>
      </table>
    </div>
    <p className="generator-overlap-footnote">SAFE +45 jest domyślną twardą regułą i działa bez żadnego zapisu z panelu. +30 to świadome awaryjne poluzowanie; kliknięcie przełącza je od razu.</p>
  </section>;
}